/**
 * Shared definitions for the Local Inventory Ads insight tables (Popular
 * products, Local search terms, Clicks to in-store sales).
 *
 * The Results and Ads pages render the top few rows of each as a summary card
 * with a "View more" button; the dedicated `/ads/insights/:metric` page renders
 * the same table in full. Keeping the title / sub / columns in one place stops
 * the summary card and its detail page from drifting apart.
 *
 * `columns` may be either a static array or a factory `(ctx) => Array` — the
 * Clicks-to-sales metric needs the factory form so the Product / Revenue cells
 * can format prices in the shop's currency (NEA-5963).
 */
import { formatCurrencyExact } from '@/lib/currency';

const numberFormatter = new Intl.NumberFormat('en-GB');
export const formatInsightNumber = (n) => numberFormatter.format(Number(n) || 0);

// Number of rows each summary card shows before the "View more" button. The
// detail page shows the full range (capped server-side in local-ads.js).
export const SUMMARY_ROWS = 6;

// Money here delegates to `@/lib/currency` so the whole app renders one symbol
// style. These used to build their own Intl formatters without
// `currencyDisplay: 'narrowSymbol'`, which put `US$45.00` from the
// Clicks-to-sales card directly under `$0.50` from the CPC tile on the same
// Ads page for a US shop (NEA-6523).
export const formatMoney = (amount, currency) => formatCurrencyExact(amount, currency);

// Whole-unit variant for the editable average-basket-value pill, which the
// design renders without decimals ("£45").
export const formatWholeMoney = (amount, currency) => formatCurrencyExact(amount, currency, 0);

// Short-day date formatter for the Date column ("12 Jun"). YYYY-MM-DD strings
// are parsed as UTC so a shop's local timezone can't shift the day.
const dateFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
export const formatShortDate = (isoDate) => {
	if (!isoDate) return '';
	const parsed = new Date(`${isoDate}T00:00:00Z`);
	if (Number.isNaN(parsed.getTime())) return '';
	return dateFormatter.format(parsed);
};

/**
 * Resolve a metric's columns, whether it's a static array or a factory that
 * needs shop context. Callers pass the `{ currency }` context; other metrics
 * ignore it.
 */
export const resolveColumns = (metric, ctx) => (typeof metric.columns === 'function' ? metric.columns(ctx || {}) : metric.columns);

export const LIA_INSIGHT_METRICS = {
	products: {
		dataKey: 'topProducts',
		title: 'Popular products',
		sub: 'What locals are clicking on most from your ads.',
		emptyLabel: 'No clicks yet — give it a couple of days.',
		columns: [
			{ key: 'name', label: 'Product' },
			{ key: 'impressions', label: 'Views', width: '5rem', render: (r) => formatInsightNumber(r.impressions) },
			{ key: 'clicks', label: 'Clicks', width: '4rem', render: (r) => formatInsightNumber(r.clicks) }
		]
	},
	'search-terms': {
		dataKey: 'searchTerms',
		title: 'Local search terms',
		sub: 'What people typed into Google to find your store.',
		emptyLabel: 'No search-term data yet.',
		columns: [
			{
				key: 'term',
				label: 'Search term',
				render: (r) => <span className="block truncate text-sm font-medium">&ldquo;{r.term}&rdquo;</span>
			},
			{ key: 'impressions', label: 'Views', width: '5rem', render: (r) => formatInsightNumber(r.impressions) },
			{ key: 'clicks', label: 'Clicks', width: '4rem', render: (r) => formatInsightNumber(r.clicks) }
		]
	},
	'clicks-to-sales': {
		dataKey: 'clicksToSales',
		title: 'Clicks to in-store sales',
		sub: 'Products that were clicked from your ads and then bought in store within 24 hours.',
		emptyLabel: 'No in-store sales attributed yet.',
		// `emphasis: true` on `sales` matches the 2026-07-06 design's bolded
		// column (the retailer's headline metric — how many things sold).
		// Date renders in a muted tone rather than being auto-bolded because
		// it's the last column but a secondary detail, not the headline.
		columns: ({ currency } = {}) => [
			{
				key: 'product',
				label: 'Product',
				render: (r) => (
					<div className="min-w-0">
						<div className="truncate text-sm font-medium text-foreground">{r.product}</div>
						{r.price > 0 ? (
							<div className="mt-0.5 text-xs text-muted-foreground">{formatMoney(r.price, currency)} each</div>
						) : null}
					</div>
				)
			},
			{ key: 'clicks', label: 'Clicks', width: '4rem', render: (r) => formatInsightNumber(r.clicks) },
			{ key: 'sales', label: 'Sales', width: '4rem', emphasis: true, render: (r) => formatInsightNumber(r.sales) },
			{ key: 'revenue', label: 'Revenue', width: '6rem', render: (r) => formatMoney(r.revenue, currency) },
			{
				key: 'salesDate',
				label: 'Date',
				width: '5rem',
				render: (r) => <span className="text-muted-foreground">{formatShortDate(r.salesDate)}</span>
			}
		]
	}
};
